export class Util {
    public static isNullOrWhitespace(t: string | null | undefined): t is null | undefined {
        return Util.isNullOrUndefined(t) || /^\s*$/gm.test(t);
    }

    public static isNotNullOrWhitespace(t: string | null | undefined): t is string {
        return typeof t === 'string' && t.trim() !== '';
    }

    public static isNullOrUndefined<T>(t: T | null | undefined): t is null | undefined {
        return t === null || t === undefined;
    }

    public static removeWhitespace(value: string): string {
        return value.replace(/\s/g, '');
    }

    public static intersectEqual<T>(left: Array<T>, right: Array<T>): boolean {
        if (left.length !== right.length) return false;

        const a = left.concat().sort();
        const b = right.concat().sort();

        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return false;
        }

        return true;
    }

    public static sequenceEqual<T>(left: Array<T>, right: Array<T>): boolean {
        if (left.length !== right.length) return false;

        for (let i = 0; i < left.length; i++) {
            if (left[i] !== right[i]) return false;
        }

        return true;
    }

    public static now() {
        return new Date();
    }

    public static formatDateAsMMddyyyy(date: Date | null, separator = '-') {
        if (date === null) return '';

        const year = date.getFullYear();
        const month = (1 + date.getMonth()).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');

        return month + separator + day + separator + year;
    }

    public static fromDateString(dateStr?: string | null | undefined): Date | null | undefined {
        if (dateStr === undefined) return undefined;
        if (dateStr === null) return null;
        if (typeof dateStr === 'object' && <unknown>dateStr instanceof Date) return dateStr;
        return new Date(dateStr);
    }

    public static toISOStringLocal(date: Date | null | undefined): string | null | undefined {
        if (date === undefined) return undefined;
        if (date === null) return null;
        return date.getFullYear() +
        '-' + Util.pad(date.getMonth() + 1) +
        '-' + Util.pad(date.getDate()) +
        'T' + Util.pad(date.getHours()) +
        ':' + Util.pad(date.getMinutes()) +
        ':' + Util.pad(date.getSeconds()) +
        Util.getTimezoneOffset(date);
    }

    // Pad a number to 2 digits
    static pad(n: number) {
        return `${Math.floor(Math.abs(n))}`.padStart(2, '0')
    }

    // Get timezone offset in ISO format (+hh:mm or -hh:mm)
    static getTimezoneOffset(date: Date) {
      const tzOffset = -date.getTimezoneOffset();
      const diff = tzOffset >= 0 ? '+' : '-';
      return diff + Util.pad(tzOffset / 60) + ':' + Util.pad(tzOffset % 60);
    }

    public static hoursOn12HourClock(date: Date) {
        const hours = date.getHours();
        const ampm = hours < 12 ? 'AM' : 'PM';
        const hours12Hour = Util.pad(date.getHours() % 12 || 12);
        const minutes = Util.pad(date.getMinutes());
        return `${hours12Hour}:${minutes} ${ampm}`;
    }

    public static integerCharactersOnly(keydown: KeyboardEvent) {
        if ('Tab,Enter,ArrowLeft,ArrowRight,ArrowUp,ArrowDown,Backspace,Home,End,Delete'.split(',').includes(keydown.key)) {
            return;
        }

        if (!('0,1,2,3,4,5,6,7,8,9'.split(',').includes(keydown.key))) {
            keydown.preventDefault();
            return;
        }

        return;
    }

    public static numericCharactersOnly(keydown: KeyboardEvent) {
        if ('Tab,Enter,ArrowLeft,ArrowRight,ArrowUp,ArrowDown,Backspace,Home,End,Delete'.split(',').includes(keydown.key)) {
            return;
        }

        if (keydown.ctrlKey && 'A,a,z,Z'.split(',').includes(keydown.key)) {
            return;
        }

        if (!('-,0,1,2,3,4,5,6,7,8,9,.'.split(',').includes(keydown.key))) {
            keydown.preventDefault();
            return;
        }
    }

    public static numberWithCommas(value: number) {
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    /**
     * Source: https://stackoverflow.com/a/8809472/15804319
     * @returns a UUID
     */
    public static generateUUID() { // Public Domain/MIT
        let d = new Date().getTime(); //Timestamp
        let d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0; //Time in microseconds since page-load or 0 if unsupported

        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            let r = Math.random() * 16;//random number between 0 and 16
            if (d > 0) {//Use timestamp until depleted
                r = (d + r) % 16 | 0;
                d = Math.floor(d / 16);
            } else {//Use microseconds since page-load if supported
                r = (d2 + r) % 16 | 0;
                d2 = Math.floor(d2 / 16);
            }
            return (c === 'x' ? r : (r & 0x7 | 0x8)).toString(16);
        });
    }

    public static IsTrue(condition: () => boolean): Promise<unknown> {
        function conditionIsTrue() {
            try {
                return condition();
            } catch {
                return false;
            }
        }

        return new Promise((resolve, reject, maxWait_ms = 10_000) => {
            const start = Date.now();

            if (conditionIsTrue()) {
                resolve(true);
            }
            else {
                const handle = setInterval(function() {
                    if (conditionIsTrue()) {
                        clearInterval(handle)
                        resolve(true);
                    }
                    else if (maxWait_ms < (Date.now() - start)) {
                        clearInterval(handle);
                        reject(`Waited longer than allowed duration of ${maxWait_ms}ms`);
                    }
                }, 400)
            }
        })
    }
}
